import React from 'react';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { Square, Columns2 } from 'lucide-react';

import Responsive from 'src/components/responsive';
import { analyticsClient, AnalyticsEvent } from 'src/services/analytics';
import {
    useIsConversationDrawerSettingEnabled,
    useIsConversationDrawerSupported,
} from 'src/pages/landlord/conversations/components/drawer/use-conversation-drawer';

import styles from 'src/pages/landlord/conversations/components/header/ConversationLayoutToggle.module.css';

type LayoutValue = 'none' | 'vertical';

/**
 * Gmail-style split-pane toggle for the conversation drawer preference. This is a per-browser,
 * local-only setting (persisted in IndexedDB, not synced across devices or users), so it is
 * deliberately surfaced here in the inbox view itself rather than in app-wide Settings.
 */
export default function ConversationLayoutToggle() {
    const isSupported = useIsConversationDrawerSupported();
    const { isEnabled, isReady, setEnabled } = useIsConversationDrawerSettingEnabled();

    if (!isSupported) {
        return null;
    }

    const value: LayoutValue = isEnabled ? 'vertical' : 'none';

    const handleChange = (_: React.MouseEvent<HTMLElement>, next: LayoutValue | null) => {
        if (!next) {
            return;
        }
        setEnabled(next === 'vertical');
        analyticsClient.track(AnalyticsEvent.CONVERSATION_DRAWER_SETTING_CHANGED, { enabled: next === 'vertical' });
    };

    return (
        <div className={styles.root}>
            <Responsive desktop>
                <Typography variant="caption" color="text.secondary" className={styles.label}>Layout</Typography>
            </Responsive>
            <ToggleButtonGroup
                size="small"
                exclusive
                value={value}
                onChange={handleChange}
                disabled={!isReady}
                aria-label="Conversation layout"
                data-testid="conversation-layout-toggle"
                className={styles.group}
            >
                <Tooltip title="No split — conversations open on their own page">
                    <ToggleButton value="none" aria-label="No split" data-testid="conversation-layout-toggle-none">
                        <Square size={18} />
                    </ToggleButton>
                </Tooltip>
                <Tooltip title="Vertical split — conversations open in a side panel">
                    <ToggleButton value="vertical" aria-label="Vertical split" data-testid="conversation-layout-toggle-vertical">
                        <Columns2 size={18} />
                    </ToggleButton>
                </Tooltip>
            </ToggleButtonGroup>
        </div>
    );
}
